// V4.7 P1 — avatar dropdown is THE account hub: profile, settings,
// language (submenu, moved out of the header), Pro upsell, referral,
// tour restart, admin, logout. The main nav stays quiet.
import React from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  User,
  Settings,
  Gift,
  Shield,
  LogOut,
  Globe,
  Check,
  Compass,
  Crown,
} from "lucide-react";
import { useAuth } from "../auth/AuthContext";
import { patchTours } from "../api/tourApi";
import { SUPPORTED_LOCALES, LOCALE_STORAGE_KEY } from "../i18n";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent,
} from "./ui/dropdown-menu";

const LANG_NAMES = { de: "Deutsch", en: "English", hr: "Hrvatski" };

export default function AvatarMenu() {
  const { user, logout, isAdmin, refreshUser } = useAuth();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  if (!user) return null;

  const initials =
    (user.display_name || user.email || "?")
      .replace(/[^A-Za-z0-9]/g, "")
      .slice(0, 2)
      .toUpperCase() || "?";

  const currentLang = (i18n.resolvedLanguage || i18n.language || "de").split("-")[0];
  const activeLang = SUPPORTED_LOCALES.includes(currentLang) ? currentLang : "de";

  const setLang = (next) => {
    if (next === activeLang) return;
    i18n.changeLanguage(next);
    try {
      window.localStorage.setItem(LOCALE_STORAGE_KEY, next);
    } catch {
      // localStorage disabled — ignore.
    }
    window.dispatchEvent(
      new CustomEvent("dealalert:locale-changed", { detail: { locale: next } }),
    );
  };

  const onLogout = async () => {
    navigate("/", { replace: true });
    await logout();
  };

  const onRestartTour = async () => {
    try {
      await patchTours({
        tour_deck_done: false,
        tour_heatmap_done: false,
        tour_ai_done: false,
      });
      await refreshUser();
      toast.success(t("profile:actions.restart_tour_done"));
      navigate("/dashboard");
    } catch {
      toast.error(t("profile:actions.restart_tour_failed"));
    }
  };

  const item = (Icon, label, onClick, testId) => (
    <DropdownMenuItem
      data-testid={testId}
      onClick={onClick}
      className="flex items-center gap-2.5 cursor-pointer focus:bg-zinc-800 focus:text-zinc-50"
    >
      <Icon size={15} strokeWidth={1.8} className="text-zinc-400" />
      {label}
    </DropdownMenuItem>
  );

  return (
    // V4.10 UX — modal={false}: the default modal dropdown adds a body
    // scroll-lock + pointer-events:none that (a) shifted/hid the sticky
    // header and (b) left the page scroll-locked after open/close on the
    // non-scrolling AI workspace.
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          data-testid="avatar-menu-trigger"
          aria-label={t("nav.my_profile_aria")}
          className="flex h-9 w-9 items-center justify-center rounded-full bg-emerald-500/15 text-[11px] font-semibold uppercase tracking-wide text-emerald-300 ring-1 ring-emerald-500/30 transition hover:ring-emerald-400/60"
        >
          {initials}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-60 border-zinc-800 bg-zinc-900 text-zinc-200">
        <DropdownMenuLabel className="truncate text-xs font-normal text-zinc-500">
          {user.email}
        </DropdownMenuLabel>
        <DropdownMenuSeparator className="bg-zinc-800" />
        {item(User, t("nav.profile", { defaultValue: "Profil" }), () => navigate("/profile"), "avatar-menu-profile")}
        {item(Settings, t("nav.settings", { defaultValue: "Einstellungen" }), () => navigate("/preferences"), "avatar-menu-settings")}
        {!user.is_pro &&
          item(Crown, t("nav.go_pro", { defaultValue: "Pro werden" }), () => navigate("/profile"), "avatar-menu-pro")}
        {item(Gift, t("nav.share_and_earn"), () => navigate("/share-and-earn"), "avatar-menu-referral")}

        <DropdownMenuSub>
          <DropdownMenuSubTrigger
            data-testid="avatar-menu-language"
            className="flex items-center gap-2.5 cursor-pointer focus:bg-zinc-800 focus:text-zinc-50 data-[state=open]:bg-zinc-800"
          >
            <Globe size={15} strokeWidth={1.8} className="text-zinc-400" />
            {t("language.label", { defaultValue: "Sprache" })}
          </DropdownMenuSubTrigger>
          <DropdownMenuSubContent className="w-40 border-zinc-800 bg-zinc-900 text-zinc-200">
            {SUPPORTED_LOCALES.map((code) => (
              <DropdownMenuItem
                key={code}
                data-testid={`language-option-${code}`}
                onClick={() => setLang(code)}
                className="flex items-center justify-between cursor-pointer focus:bg-zinc-800 focus:text-zinc-50"
              >
                {LANG_NAMES[code]}
                {code === activeLang && <Check size={14} className="text-emerald-400" />}
              </DropdownMenuItem>
            ))}
          </DropdownMenuSubContent>
        </DropdownMenuSub>

        {item(Compass, t("profile:actions.restart_tour"), onRestartTour, "avatar-menu-restart-tour")}
        {isAdmin && item(Shield, t("nav.admin"), () => navigate("/admin"), "avatar-menu-admin")}
        <DropdownMenuSeparator className="bg-zinc-800" />
        {item(LogOut, t("nav.logout"), onLogout, "avatar-menu-logout")}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
